// src/contexts/AuthContext.tsx

import { identify } from "@sailfish-ai/recorder";
import Cookies from "js-cookie";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCurrentUserLazyQuery } from "../__generated__/graphql";
import {
  COOKIE_DOMAIN_OPTIONS,
  isTokenExpired as isTokenExpiredFn,
} from "../utils/auth";
import { JWT_KEY } from "../utils/constants";
import { verifyToken } from "../utils/jwt";

interface AuthContextType {
  isAuthenticated: boolean;
  setAuthenticated: (value: boolean) => void;
  user: UserType | null;
  setUser: (user: UserType | null) => void;
  redirectPath: string;
  setRedirectPath: (path: string) => void;
  isSailfishQAMember: boolean;
  isTokenExpired: boolean;
}

interface UserType {
  name: string;
  picture: string;
  email: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserType | null>(null);
  const [redirectPath, setRedirectPath] = useState<string>("/");
  const [isSailfishQAMember, setIsSailfishQAMember] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [fetchUser] = useCurrentUserLazyQuery();

  useEffect(() => {
    const checkAuthAndFetchUser = async () => {
      const tokenString = Cookies.get(JWT_KEY);
      let verifiedPayload = null;

      if (tokenString) {
        try {
          verifiedPayload = await verifyToken(tokenString);
          setIsAuthenticated(true);
        } catch (verificationError) {
          if (verificationError != '"exp" claim timestamp check failed')
            console.warn("JWT verification failed:", verificationError);
          setIsAuthenticated(false);
          Cookies.remove(JWT_KEY, COOKIE_DOMAIN_OPTIONS);
        }
      } else {
        setIsAuthenticated(false);
      }

      if (verifiedPayload) {
        // If verification passed, restore user from storage
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsedStoredUser = JSON.parse(storedUser) as UserType;
          setUser(parsedStoredUser);
          setIsSailfishQAMember(
            parsedStoredUser.email.endsWith("@sailfishqa.com"),
          );
          identify(parsedStoredUser.email, { foo: "bar" }, false);
        }
      } else if (location.pathname !== "/login") {
        // Not verified: redirect to login
        localStorage.setItem("redirectAfterLogin", location.pathname);
        Cookies.remove(JWT_KEY, COOKIE_DOMAIN_OPTIONS);
        localStorage.removeItem("user");
        setUser(null);
        navigate("/login");
      }
    };

    checkAuthAndFetchUser();
  }, [location, navigate, fetchUser]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setAuthenticated: setIsAuthenticated,
        user,
        setUser,
        redirectPath,
        setRedirectPath,
        isSailfishQAMember,
        isTokenExpired: isTokenExpiredFn(Cookies.get(JWT_KEY)),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
